import { zodResolver } from '@hookform/resolvers/zod'
import { Check, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useState } from 'react'
import { useForm, Controller, type SubmitHandler } from 'react-hook-form'
import { z } from 'zod'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useDepartments,
  type FlatDepartment,
  useOnboarding,
  type OnboardingInput,
  type TeamMemberType,
  type DeveloperGroup,
  type FunctionRole,
  useActiveEmployees,
  type CompanyRole,
  usePositions,
  type FlatPosition,
  useTeams,
  type Team,
} from '@/hooks'
import { cn } from '@/lib/utils'

interface OnboardingWizardProps {
  onClose: () => void
  onSuccess?: () => void
}

const STEPS = [
  { id: 1, title: '基本信息', description: '姓名、邮箱、入职日期' },
  { id: 2, title: '组织分配', description: '部门、职位、角色' },
  { id: 3, title: '团队分配', description: '团队、团队角色' },
  { id: 4, title: '薪资设置', description: '基本工资、津贴' },
  { id: 5, title: '假期初始化', description: '年假、病假等' },
]

const ROLE_OPTIONS: { value: CompanyRole; label: string }[] = [
  { value: 'director', label: '主管' },
  { value: 'finance', label: '财务' },
  { value: 'hr', label: '人事' },
  { value: 'admin', label: '行政' },
  { value: 'pm', label: '项目经理' },
  { value: 'developer', label: '开发人员' },
]

const MEMBER_TYPE_OPTIONS: { value: TeamMemberType; label: string }[] = [
  { value: 'developer', label: '开发人员' },
  { value: 'function', label: '职能人员' },
]

const DEVELOPER_GROUP_OPTIONS: { value: DeveloperGroup; label: string }[] = [
  { value: 'product', label: '产品' },
  { value: 'design', label: '设计' },
  { value: 'frontend', label: '前端' },
  { value: 'backend', label: '后端' },
  { value: 'testing', label: '测试' },
]

const FUNCTION_ROLE_OPTIONS: { value: FunctionRole; label: string }[] = [
  { value: 'finance', label: '财务' },
  { value: 'hr', label: '人事' },
  { value: 'admin', label: '行政' },
]

const DEFAULT_LEAVE_BALANCES = [
  { type: 'annual', label: '年假', total: 15 },
  { type: 'sick', label: '病假', total: 10 },
  { type: 'personal', label: '事假', total: 5 },
]

const formSchema = z.object({
  basicInfo: z.object({
    name: z.string().min(1, '请输入姓名'),
    email: z.string().min(1, '请输入邮箱').email('邮箱格式不正确'),
    phone: z.string().optional(),
    hireDate: z.string().min(1, '请选择入职日期'),
  }),
  organization: z.object({
    role: z.enum(['director', 'finance', 'hr', 'admin', 'pm', 'developer'] as const, {
      required_error: '请选择角色',
    }),
    departmentId: z.string().optional(),
    positionId: z.string().optional(),
    managerId: z.string().optional(),
  }),
  teamAssignment: z.object({
    teamId: z.string().optional(),
    memberType: z.enum(['developer', 'function'] as const),
    developerGroup: z.enum(['product', 'design', 'frontend', 'backend', 'testing'] as const).optional(),
    functionRole: z.enum(['finance', 'hr', 'admin'] as const).optional(),
    isLeader: z.boolean().default(false),
  }),
  salary: z.object({
    baseSalary: z.string().optional(), // Keep as string for input, parse on submit
    allowance: z.string().optional(),
    effectiveDate: z.string().optional(), // Default to today in component
  }),
  leaveBalances: z.array(z.object({
    type: z.string(),
    label: z.string(),
    total: z.number().min(0),
  })),
})

type OnboardingFormValues = z.infer<typeof formSchema>




export function OnboardingWizard({ onClose, onSuccess }: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const onboardingMutation = useOnboarding()

  const { data: departments } = useDepartments()
  const { data: positions } = usePositions()
  const { data: activeEmployees } = useActiveEmployees()
  const { data: teamsData } = useTeams()

  const form = useForm<OnboardingFormValues>({
    resolver: zodResolver(formSchema) as any, // Type assertion to bypass strict optionality mismatch in current RHF/Zod version combination
    defaultValues: {
      basicInfo: {
        name: '',
        email: '',
        phone: '',
        hireDate: new Date().toISOString().split('T')[0],
      },
      organization: {
        role: undefined, // Will leverage required validation
        departmentId: '',
        positionId: '',
        managerId: '',
      },
      teamAssignment: {
        teamId: '',
        memberType: 'developer',
        isLeader: false,
      },
      salary: {
        baseSalary: '',
        allowance: '',
        effectiveDate: new Date().toISOString().split('T')[0],
      },
      leaveBalances: DEFAULT_LEAVE_BALANCES.map((lb) => ({ ...lb })),
    },
    mode: 'onChange',
  })

  // Navigation Logic
  const handleNext = async () => {
    let fields: any[] = []
    switch (currentStep) {
      case 1:
        fields = ['basicInfo.name', 'basicInfo.email', 'basicInfo.hireDate']
        break
      case 2: // Organization step - theoretically optional but if role is selected, it's valid? Schema says role is required.
        // Actually schema says role is required enum. Department/Position optional.
        fields = ['organization.role']
        break
      case 3: // Team step - mostly optional/defaults
        fields = ['teamAssignment']
        break
      case 4: // Salary
        // Logic checks if fields are filled. Schema fields are optional strings.
        // If user partially fills, maybe we want to validate? 
        // For now, schema allows empty strings which are optional. 
        // But if we want to enforce numbers if filled:
        // We'll let the user proceed as they are defined as optional/string in schema.
        break
    }

    const valid = await form.trigger(fields)
    if (valid) {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length))
    }
  }

  const handlePrev = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1))
  }

  const onSubmit: SubmitHandler<OnboardingFormValues> = (data) => {
    const input: OnboardingInput = {
      basicInfo: {
        name: data.basicInfo.name,
        email: data.basicInfo.email,
        phone: data.basicInfo.phone || undefined,
        hireDate: data.basicInfo.hireDate,
      },
    }

    // Organization
    if (data.organization.departmentId || data.organization.positionId || data.organization.role) {
      // Logic: If user enters organization step, role is required by schema. 
      // But we need to make sure we don't send empty strings as IDs if the API expects undefined.
      // Assuming API handles empty strings or we strip them.
      // Based on original logic:
      if (data.organization.departmentId || data.organization.positionId) {
        input.organization = {
          role: data.organization.role,
          departmentId: data.organization.departmentId || '', // Check if API allows empty string? 
          // Original logic: value === 'placeholder' ? '' : value
          // If it's empty string, let's pass it if API allows, or undefined.
          // Let's look at `OnboardingInput` definition in original file.
          // It seems original logic just passed internal state.
          // We'll assume empty strings are okay or cleaned up by backend, 
          // BUT original code used `|| undefined` in some places?
          // Line 161: `managerId: organization.managerId || undefined`
          // Let's match that.
          positionId: data.organization.positionId || '',
          managerId: data.organization.managerId || undefined,
        }
      }
    }

    // Teams
    if (data.teamAssignment.teamId) {
      input.teams = [{
        teamId: data.teamAssignment.teamId,
        memberType: data.teamAssignment.memberType,
        developerGroup: data.teamAssignment.developerGroup,
        functionRole: data.teamAssignment.functionRole,
        isLeader: data.teamAssignment.isLeader,
      }]
    }

    // Salary - Convert string to number (cents)
    if (data.salary.baseSalary) {
      input.salary = {
        baseSalary: Math.round(parseFloat(data.salary.baseSalary) * 100),
        allowance: data.salary.allowance ? Math.round(parseFloat(data.salary.allowance) * 100) : 0,
        effectiveDate: data.salary.effectiveDate as string,
      }
    }

    // Leave Balances
    input.leaveBalances = data.leaveBalances.map(lb => ({
      type: lb.type,
      total: lb.total
    }))

    onboardingMutation.mutate(input, {
      onSuccess: () => {
        onSuccess?.()
        onClose()
      },
    })
  }

  const teams = teamsData ?? []

  // Helper for errors
  const getError = (path: string) => {
    // react-hook-form errors are nested
    // e.g. errors.basicInfo?.name
    const parts = path.split('.')
    let current: any = form.formState.errors
    for (const part of parts) {
      current = current?.[part]
    }
    return current?.message as string | undefined
  }

  // Step content renderers
  const renderBasicInfoStep = () => (
    <div className="space-y-4">
      <div>
        <Label className="mb-1.5 block text-sm font-medium">
          姓名 <span className="text-destructive">*</span>
        </Label>
        <Input
          {...form.register('basicInfo.name')}
          placeholder="请输入姓名"
          className={cn(getError('basicInfo.name') && 'border-destructive')}
        />
        {getError('basicInfo.name') && <p className="mt-1 text-sm text-destructive">{getError('basicInfo.name')}</p>}
      </div>

      <div>
        <Label className="mb-1.5 block text-sm font-medium">
          邮箱 <span className="text-destructive">*</span>
        </Label>
        <Input
          {...form.register('basicInfo.email')}
          placeholder="请输入邮箱"
          className={cn(getError('basicInfo.email') && 'border-destructive')}
        />
        {getError('basicInfo.email') && <p className="mt-1 text-sm text-destructive">{getError('basicInfo.email')}</p>}
      </div>

      <div>
        <Label className="mb-1.5 block text-sm font-medium">电话</Label>
        <Input
          {...form.register('basicInfo.phone')}
          placeholder="请输入电话"
        />
      </div>

      <div>
        <Label className="mb-1.5 block text-sm font-medium">
          入职日期 <span className="text-destructive">*</span>
        </Label>
        <Input
          type="date"
          {...form.register('basicInfo.hireDate')}
          className={cn(getError('basicInfo.hireDate') && 'border-destructive')}
        />
        {getError('basicInfo.hireDate') && <p className="mt-1 text-sm text-destructive">{getError('basicInfo.hireDate')}</p>}
      </div>
    </div>
  )


  const renderOrganizationStep = () => (
    <div className="space-y-4">
      <div>
        <Label className="mb-1.5 block text-sm font-medium">角色</Label>
        <Controller
          control={form.control}
          name="organization.role"
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="请选择角色" />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {getError('organization.role') && <p className="mt-1 text-sm text-destructive">{getError('organization.role')}</p>}
      </div>

      <div>
        <Label className="mb-1.5 block text-sm font-medium">部门</Label>
        <Controller
          control={form.control}
          name="organization.departmentId"
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="请选择部门" />
              </SelectTrigger>
              <SelectContent>
                {departments?.map((dept: FlatDepartment) => (
                  <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      <div>
        <Label className="mb-1.5 block text-sm font-medium">职位</Label>
        <Controller
          control={form.control}
          name="organization.positionId"
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="请选择职位" />
              </SelectTrigger>
              <SelectContent>
                {positions?.map((pos: FlatPosition) => (
                  <SelectItem key={pos.id} value={pos.id}>{pos.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      <div>
        <Label className="mb-1.5 block text-sm font-medium">直属上级</Label>
        <Controller
          control={form.control}
          name="organization.managerId"
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="请选择上级" />
              </SelectTrigger>
              <SelectContent>
                {activeEmployees?.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>
    </div>
  )

  const renderTeamStep = () => {
    const teamAssignment = form.watch('teamAssignment')

    return (
      <div className="space-y-4">
        <div>
          <Label className="mb-1.5 block text-sm font-medium">团队</Label>
          <Controller
            control={form.control}
            name="teamAssignment.teamId"
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="请选择团队（可选）" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="placeholder">请选择团队（可选）</SelectItem>
                  {teams.map((team: Team) => (
                    <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        {teamAssignment.teamId && teamAssignment.teamId !== 'placeholder' && (
          <>
            <div>
              <Label className="mb-1.5 block text-sm font-medium">成员类型</Label>
              <Controller
                control={form.control}
                name="teamAssignment.memberType"
                render={({ field }) => (
                  <Select
                    onValueChange={(val) => {
                      field.onChange(val)
                      // Reset dependent fields
                      form.setValue('teamAssignment.developerGroup', undefined)
                      form.setValue('teamAssignment.functionRole', undefined)
                    }}
                    value={field.value}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="请选择成员类型" />
                    </SelectTrigger>
                    <SelectContent>
                      {MEMBER_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {teamAssignment.memberType === 'developer' && (
              <div>
                <Label className="mb-1.5 block text-sm font-medium">开发组</Label>
                <Controller
                  control={form.control}
                  name="teamAssignment.developerGroup"
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="请选择开发组" />
                      </SelectTrigger>
                      <SelectContent>
                        {DEVELOPER_GROUP_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            )}

            {teamAssignment.memberType === 'function' && (
              <div>
                <Label className="mb-1.5 block text-sm font-medium">职能角色</Label>
                <Controller
                  control={form.control}
                  name="teamAssignment.functionRole"
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="请选择职能角色" />
                      </SelectTrigger>
                      <SelectContent>
                        {FUNCTION_ROLE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isLeader"
                {...form.register('teamAssignment.isLeader')}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="isLeader" className="text-sm">设为团队负责人</Label>
            </div>
          </>
        )}

        {(!teamAssignment.teamId || teamAssignment.teamId === 'placeholder') && (
          <p className="text-sm text-muted-foreground">可以跳过此步骤，稍后再分配团队</p>
        )}
      </div>
    )
  }

  const renderSalaryStep = () => {
    const salaryBase = form.watch('salary.baseSalary')
    return (
      <div className="space-y-4">
        <div>
          <Label className="mb-1.5 block text-sm font-medium">基本工资（元/月）</Label>
          <Input
            type="number"
            {...form.register('salary.baseSalary')}
            placeholder="请输入基本工资"
            min="0"
            step="0.01"
          />
        </div>

        <div>
          <Label className="mb-1.5 block text-sm font-medium">津贴（元/月）</Label>
          <Input
            type="number"
            {...form.register('salary.allowance')}
            placeholder="请输入津贴"
            min="0"
            step="0.01"
          />
        </div>

        <div>
          <Label className="mb-1.5 block text-sm font-medium">生效日期</Label>
          <Input
            type="date"
            {...form.register('salary.effectiveDate')}
          />
        </div>

        {!salaryBase && (
          <p className="text-sm text-muted-foreground">可以跳过此步骤，稍后再设置薪资</p>
        )}
      </div>
    )
  }

  const renderLeaveStep = () => {
    const leaveBalances = form.watch('leaveBalances')
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">设置员工的初始假期余额（天）</p>

        {leaveBalances.map((lb, index) => (
          <div key={lb.type} className="flex items-center gap-4">
            <Label className="w-20 text-sm font-medium">{lb.label}</Label>
            <Input
              type="number"
              {...form.register(`leaveBalances.${index}.total`, { valueAsNumber: true })}
              min="0"
              className="w-24"
            />
            <span className="text-sm text-muted-foreground">天</span>
          </div>
        ))}
      </div>
    )
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderBasicInfoStep()
      case 2:
        return renderOrganizationStep()
      case 3:
        return renderTeamStep()
      case 4:
        return renderSalaryStep()
      case 5:
        return renderLeaveStep()
      default:
        return null
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-lg bg-background shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">员工入职向导</h2>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium',
                      currentStep > step.id
                        ? 'bg-primary text-primary-foreground'
                        : currentStep === step.id
                          ? 'border-2 border-primary text-primary'
                          : 'border-2 border-muted text-muted-foreground'
                    )}
                  >
                    {currentStep > step.id ? <Check className="h-4 w-4" /> : step.id}
                  </div>
                  <span
                    className={cn(
                      'mt-1 text-xs',
                      currentStep >= step.id ? 'text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    {step.title}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={cn(
                      'mx-2 h-0.5 w-12',
                      currentStep > step.id ? 'bg-primary' : 'bg-muted'
                    )}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="max-h-[50vh] overflow-y-auto p-6">
          <div className="mb-4">
            <h3 className="text-base font-medium">{STEPS[currentStep - 1].title}</h3>
            <p className="text-sm text-muted-foreground">{STEPS[currentStep - 1].description}</p>
          </div>
          {renderStepContent()}

          {onboardingMutation.error && (
            <div className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {onboardingMutation.error.message}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t px-6 py-4">
          <button
            type="button"
            onClick={handlePrev}
            disabled={currentStep === 1}
            className="flex items-center gap-1 rounded-md px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" />
            上一步
          </button>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              取消
            </button>

            {currentStep < STEPS.length ? (
              <button
                type="button"
                onClick={() => void handleNext()}
                className="flex items-center gap-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                下一步
                <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void form.handleSubmit(onSubmit)()}
                disabled={onboardingMutation.isPending}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {onboardingMutation.isPending ? '提交中...' : '完成入职'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
